/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { BusinessPartner } from '@msdyn365-commerce/retail-proxy';
import { Button, IDataTableProps, ITableRowProps, ITableItemProps, Table, TableDataType } from '@msdyn365-commerce-modules/utilities';
import { IRequestContextUrl } from '@msdyn365-commerce/core';

import { IOrganizationSelectionProps } from '../organization-selection.props.autogenerated';
import { IOrganizationSelectionState } from '..';

export interface IOrganizationsTableProps extends IOrganizationSelectionState, IOrganizationSelectionProps<{}> {
    moduleClassName: string;
    organizationSelectionCookieKey: string;
    getCurrentUrl: ({ requestUrl: { href } }: IRequestContextUrl) => URL;
    toggleModal: (action: 'open' | 'close', organization?: BusinessPartner) => void;
    selectOrganization: (organization: BusinessPartner) => Promise<void>;
}

export enum Columns {
    Address = 'Address',
    OrganizationName = 'Organization Name',
    SpendingLimit = 'Spending Limit'
}

interface ITableCellProps {
    value: string | number;
    organization: BusinessPartner;
    classNameSuffix: string;
    isOrganizationName?: boolean;
    currentAccountNumber?: string;
}

/**
 * OrganizationsTable component.
 * @extends {React.Component<IOrganizationsTableProps>}
 */
export class OrganizationsTable extends React.Component<IOrganizationsTableProps> {
    private readonly _mobileExcludedColumns: string[] = [Columns.Address];
    private readonly _addressColumnConfigValue: string = 'address';
    private readonly _spendingLimitColumnConfigValue: string = 'spendingLimit';

    constructor(props: IOrganizationsTableProps) {
        super(props);
        this._selectOrganization = this._selectOrganization.bind(this);
    }

    public render(): JSX.Element {
        return <Table {...this._createDataGridProps()} />;
    }

    private readonly _createDataGridProps = (): IDataTableProps => {
        const {
            config,
            context: {
                request: { query, url }
            },
            isMobile,
            moduleClassName,
            resources
        } = this.props;
        const {
            paginationItemPerPage,
            showPagination,
            tableSort,
            organizationNameHeaderText,
            addressHeaderText,
            organizationColumnsToBeShown,
            spendingLimitHeaderText
        } = config;
        const {
            columnSortAriaLabel,
            organizationPaginationAriaLabel,
            organizationPaginationNextButtonText,
            organizationPaginationPreviousButtonText,
            sortByAscendingAriaLabel,
            sortByDescendingAriaLabel,
            tableAriaLabel
        } = resources;

        const tableItems = this._createDataGridItems();
        const headings = tableItems[0]?.row?.map(({ id }) => {
            let columnHeaderName = '';
            switch (id) {
                case Columns.OrganizationName:
                    columnHeaderName = organizationNameHeaderText || id;
                    break;
                case Columns.Address:
                    columnHeaderName = addressHeaderText || id;
                    break;
                case Columns.SpendingLimit:
                    columnHeaderName = spendingLimitHeaderText || id;
                    break;
                default:
                    columnHeaderName = id;
                    break;
            }
            // Return undefined if the column should be excluded based on mobile view
            if (isMobile && this._mobileExcludedColumns.includes(id)) {
                return undefined;
            }

            // Return undefined if the column is not in the columns to be shown site builder configuration
            if (
                organizationColumnsToBeShown &&
                organizationColumnsToBeShown.indexOf(this._addressColumnConfigValue) === -1 &&
                id === Columns.Address
            ) {
                return undefined;
            }
            if (
                organizationColumnsToBeShown &&
                organizationColumnsToBeShown.indexOf(this._spendingLimitColumnConfigValue) === -1 &&
                id === Columns.SpendingLimit
            ) {
                return undefined;
            }

            return {
                name: columnHeaderName,
                sortable: tableSort === true,
                ariaLabel: columnSortAriaLabel
            };
        });

        return {
            actions: {},
            className: `${moduleClassName}__table`,
            enableToModify: false,
            excludedColumns: isMobile ? this._mobileExcludedColumns : [],
            headings: headings ?? [],
            isSingleSelectOnly: true,
            isSortable: tableSort,
            paginationProperty: {
                itemPerPage: paginationItemPerPage || 10,
                nextText: organizationPaginationNextButtonText,
                paginationText: organizationPaginationAriaLabel,
                prevText: organizationPaginationPreviousButtonText,
                skipCount: query?.skip && !isNaN(Number.parseInt(query.skip, 10)) ? Number.parseInt(query.skip, 10) : 0,
                url: this.props.getCurrentUrl(url).href
            },
            resourcePrefix: 'organization',
            resources,
            rows: tableItems,
            showPagination,
            sortByAscending: sortByAscendingAriaLabel,
            sortByDescending: sortByDescendingAriaLabel,
            tableAriaLabel,
            tableTabIndex: 0
        };
    };

    private readonly _createDataGridItems = (): ITableRowProps[] => {
        const { filteredOrganizationList, organizationList, config } = this.props;
        const { organizationColumnsToBeShown } = config;
        const renderList = filteredOrganizationList ?? organizationList;

        return renderList.map((organization, index) => {
            const { Addresses, Name } = organization;
            const address = Addresses?.find(address => address.IsPrimary) ?? Addresses?.[0];

            const row: ITableItemProps[] = [];
            const orgName = Name ?? '';
            row.push({
                id: Columns.OrganizationName,
                type: TableDataType.Text,
                value: orgName,
                nodeValue: this._renderCellWithButton({
                    classNameSuffix: 'organization-name',
                    isOrganizationName: true,
                    organization,
                    value: orgName
                })
            });
            const fullAddress = address?.FullAddress ?? '';
            organizationColumnsToBeShown &&
                organizationColumnsToBeShown.indexOf(this._addressColumnConfigValue) > -1 &&
                row.push({
                    id: Columns.Address,
                    type: TableDataType.Text,
                    value: fullAddress,
                    nodeValue: this._renderCellWithButton({
                        classNameSuffix: 'address',
                        organization,
                        value: fullAddress
                    })
                });
            const spendingLimit = this._formatSpendingLimit(organization.SpendingLimit ?? 0);
            organizationColumnsToBeShown &&
                organizationColumnsToBeShown.indexOf(this._spendingLimitColumnConfigValue) > -1 &&
                row.push({
                    id: Columns.SpendingLimit,
                    type: TableDataType.Text,
                    value: spendingLimit,
                    nodeValue: this._renderCellWithButton({
                        classNameSuffix: 'spending-limit',
                        organization,
                        value: spendingLimit
                    })
                });
            return {
                row: row,
                isSelected: false
            };
        });
    };

    private readonly _renderCellWithButton = ({
        classNameSuffix,
        isOrganizationName,
        organization,
        value
    }: ITableCellProps): JSX.Element => {
        const {
            currentAccountNumber,
            moduleClassName,
            resources: { currentlySelectedText }
        } = this.props;
        const { OrganizationCustomerAccountNumber } = organization;

        return (
            <div role='button' className={`${moduleClassName}__${classNameSuffix}`} onClick={() => this._selectOrganization(organization)}>
                {isOrganizationName ? (
                    <>
                        <Button className={`${moduleClassName}__btn-select`} onClick={() => this._selectOrganization(organization)}>
                            <span>{value}</span>{' '}
                            <span>{OrganizationCustomerAccountNumber === currentAccountNumber && <>({currentlySelectedText})</>}</span>
                        </Button>
                    </>
                ) : (
                    <p>{value}</p>
                )}
            </div>
        );
    };

    private readonly _selectOrganization = async (organization: BusinessPartner): Promise<void> => {
        const { toggleModal, selectOrganization, skipSwitchOrganizationMessageSelected, currentAccountNumber } = this.props;
        const { OrganizationCustomerAccountNumber } = organization;

        if (skipSwitchOrganizationMessageSelected || !currentAccountNumber) {
            if (OrganizationCustomerAccountNumber && OrganizationCustomerAccountNumber.length > 0) {
                void selectOrganization(organization);
            }
        } else {
            toggleModal('open', organization);
        }
    };

    private readonly _formatSpendingLimit = (value: number): string => {
        const { cultureFormatter } = this.props.context;
        return cultureFormatter ? cultureFormatter.formatCurrency(value) : value.toString();
    };
}
